
import { useEffect, useState } from 'react';

interface ScrollProgressBarProps {
  className?: string;
  showPercentage?: boolean;
  glowEffect?: boolean;
  particleTrail?: boolean;
}

export default function ScrollProgressBar({ 
  className = '', 
  showPercentage = false,
  glowEffect = true,
  particleTrail = true
}: ScrollProgressBarProps) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      
      setScrollProgress(Math.min(100, Math.max(0, progress)));
      setIsVisible(scrollTop > 100);
    };

    const throttledUpdate = () => {
      requestAnimationFrame(updateScrollProgress);
    };

    window.addEventListener('scroll', throttledUpdate, { passive: true });
    updateScrollProgress();

    return () => window.removeEventListener('scroll', throttledUpdate);
  }, []);

  if (!isVisible) return null;

  return (
    <>
      {/* Main Progress Bar */}
      <div 
        className={`fixed top-0 left-0 right-0 h-1 z-50 transition-all duration-300 ${className}`}
        style={{ 
          background: 'linear-gradient(90deg, transparent 0%, rgba(57, 242, 174, 0.1) 50%, transparent 100%)'
        }}
      >
        <div
          className={`h-full transition-all duration-150 ease-out relative ${
            glowEffect ? 'animate-glow-pulse' : ''
          }`}
          style={{
            width: `${scrollProgress}%`,
            background: `linear-gradient(90deg, 
              #39F2AE 0%, 
              #2B6EF3 50%, 
              #39F2AE 100%
            )`,
            boxShadow: glowEffect ? `
              0 0 20px rgba(57, 242, 174, 0.6),
              0 0 40px rgba(43, 110, 243, 0.4),
              0 2px 10px rgba(0, 0, 0, 0.1)
            ` : 'none'
          }}
        >
          {/* Animated Shimmer Effect */}
          <div 
            className="absolute inset-0 animate-shimmer"
            style={{
              background: `linear-gradient(90deg, 
                transparent, 
                rgba(255, 255, 255, 0.4), 
                transparent
              )`,
              backgroundSize: '200% 100%'
            }}
          />
          
          {/* Progress Indicator Dot */}
          <div 
            className="absolute right-0 top-1/2 transform -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg animate-pulse"
            style={{
              boxShadow: `
                0 0 10px rgba(57, 242, 174, 0.8),
                0 0 20px rgba(43, 110, 243, 0.6)
              `
            }}
          />
        </div>
      </div>

      {/* Particle Trail Effect */}
      {particleTrail && (
        <div className="fixed top-0 left-0 right-0 h-1 z-40 pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute top-0 w-2 h-2 bg-gradient-to-r from-accent to-signal rounded-full opacity-60 animate-particle-trail"
              style={{
                left: `${Math.max(0, scrollProgress - (i + 1) * 5)}%`,
                animationDelay: `${i * 100}ms`,
                animationDuration: '2s'
              }}
            />
          ))}
        </div>
      )}

      {/* Percentage Display */}
      {showPercentage && (
        <div 
          className="fixed top-4 right-4 z-50 bg-white/90 backdrop-blur-md rounded-full px-3 py-1 text-sm font-semibold shadow-lg transition-all duration-300"
          style={{
            color: `hsl(${scrollProgress * 1.2 + 180}, 70%, 50%)`,
            transform: `scale(${0.8 + scrollProgress * 0.002})`
          }}
        >
          {Math.round(scrollProgress)}%
        </div>
      )}

      {/* Dynamic Background Color */}
      <style jsx>{`
        :root {
          --scroll-hue: ${180 + scrollProgress * 0.8};
        }
      `}</style>
    </>
  );
}
